<template>
  <app-versioning-drawer
    :model-value="documentsVersioningPane.isOpen"
    :documents="documentsVersioningPane.documents"
    :can-manage-versions="canManageVersions"
    v-model:tab="tab"
    :add-disabled="addDisabled"
    :add-loading="addLoading"
    data-locator="documents-versioning-pane"
    @add="submitAddVersion"
    @close="closePane"
  >
    <template #manage>
      <DocumentVersioning
        ref="versioning"
        :loading="postVersionFromImanagePending || versionsLoading"
        :selected-documents="documentsVersioningPane.documents"
        @add-version="onAddVersion"
        @add-version-from-imanage="onAddVersionFromImanage"
        @update:add-disabled="addDisabled = $event"
        @update:add-loading="addLoading = $event"
      />
    </template>
    <template #history>
      <DocumentVersioningHistory :selected-documents="documentsVersioningPane.documents" @share-version="onShareVersion" />
    </template>
  </app-versioning-drawer>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import DocumentVersioning from '@/common/document-versioning/DocumentVersioning.vue'
import DocumentVersioningHistory from '@/common/document-versioning/DocumentVersioningHistory.vue'
import { track } from '@/common/pendo/agent'
import { toast } from '@/common/utils/toast'
import {
  ADD_VERSION,
  CHANGE_DOCUMENTS_VERSIONING_PANE_TAB,
  CLOSE_DOCUMENTS_VERSIONING_PANE,
  DESELECT_ALL_DOCUMENTS,
  GET_PAGINATED_DOCUMENTS,
  POST_VERSION_FROM_IMANAGE,
  SET_DOCUMENTS_VERSIONING_PANE,
  SET_TABLE_DOCUMENTS_PANE,
} from '@/store/modules/documents/action_types'

const TRANSLATION_KEY_PREFIX = 'project.documents.pane.DocumentsVersioningPane'

export default defineComponent({
  name: 'DocumentsVersioningPane',
  components: {
    DocumentVersioning,
    DocumentVersioningHistory,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      // Mirrors the state of the add-version form, which now drives a footer rendered outside
      // of it. Disabled until the form says otherwise.
      addDisabled: true,
      addLoading: false,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm', 'currentUserRights']),
    ...mapState('documents', ['documentsVersioningPane', 'numberingEnabled', 'postVersionFromImanagePending', 'versionsLoading']),
    canManageVersions () {
      return this.isCurrentUserPm || this.currentUserRights.canUploadVersions
    },
    tab: {
      get () {
        return this.documentsVersioningPane.tab
      },
      set (val) {
        this.CHANGE_DOCUMENTS_VERSIONING_PANE_TAB(val)
      },
    },
  },
  methods: {
    ...mapActions('documents', [
      ADD_VERSION,
      CHANGE_DOCUMENTS_VERSIONING_PANE_TAB,
      CLOSE_DOCUMENTS_VERSIONING_PANE,
      DESELECT_ALL_DOCUMENTS,
      GET_PAGINATED_DOCUMENTS,
      POST_VERSION_FROM_IMANAGE,
      SET_DOCUMENTS_VERSIONING_PANE,
      SET_TABLE_DOCUMENTS_PANE,
    ]),
    async onAddVersion (data) {
      try {
        await this.ADD_VERSION(data)

        track('new_version_created', { documentMnemo: this.mnemo, from: 'Document' })

        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.addVersionFromClosdSuccess`))

        this.GET_PAGINATED_DOCUMENTS()

        this.closePane()
      } catch (error) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.addVersionFromClosdError`))
      }
    },
    async onAddVersionFromImanage (data) {
      try {
        await this.POST_VERSION_FROM_IMANAGE({
          mnemo: this.mnemo,
          data: data,
        })
        
        track('new_version_created', { documentMnemo: this.mnemo, from: 'Document' })

        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.addVersionFromImanageSuccess`))

        this.GET_PAGINATED_DOCUMENTS()

        this.closePane()
      } catch (error) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.addVersionFromImanageError`))
      }
    },
    // The add action lives in the drawer footer, outside of the scrollable body, so it calls
    // back into the form that owns the version being built.
    submitAddVersion () {
      this.$refs.versioning?.onAddVersion()
    },
    closePane () {
      this.CLOSE_DOCUMENTS_VERSIONING_PANE()
    },
    onShareVersion (data) {
      // we have to use destructuration to break reference to the variable in the store
      const currentDocument = { ...this.documentsVersioningPane.documents[0] }

      this.CLOSE_DOCUMENTS_VERSIONING_PANE()
      this.SET_TABLE_DOCUMENTS_PANE({
        isOpen: true,
        documents: [data],
        tab: 'share',
        forcePmSelection: true,
        previousDocument: this.documentsVersioningPane.documents[0],
        onCloseCallback: () => {
          this.SET_DOCUMENTS_VERSIONING_PANE({
            isOpen: true,
            documents: [currentDocument],
            tab: 'history',
          })
        },
      })
    },
  },
})
</script>
